/**
 * What the upload paths know about their own progress, reported for the
 * upload monitor (see uploadMonitor.ts).
 *
 * Queue counts alone can't tell "slow" from "stuck": a queue that hasn't
 * shrunk in ten minutes may be one huge photo on a poor link or the same
 * request failing over and over. The connector and the attachment queue
 * already know which it is, so they say so here. Nothing in this module
 * changes how uploads run.
 *
 * Kept free of imports so the connector and the attachment queue can report
 * without pulling the monitor (or React) into their dependency graph.
 */

/**
 * How long the same upload must keep failing before it counts as stuck: the
 * connector reports a stalled queue head to Sentry after this long, and the
 * upload status shows "not making progress".
 */
export const QUEUE_HEAD_STALL_THRESHOLD_MS = 10 * 60 * 1000

export type UploadSignals = {
  /** When a row batch or a photo file last reached the server. */
  lastSuccessAt?: number
  /**
   * Row uploads can't start for want of a usable connection: the device is
   * offline, there's no live session, the request never reached the server,
   * or the server couldn't confirm the token (which, from the user's side, is
   * a connection problem too). Cleared by the next attempt that gets a real
   * answer from the server.
   */
  rowsWaitingForConnection: boolean
  /**
   * Since when row uploads have been failing with an error that keeps them
   * queued, with no upload, park or connection problem in between. Counted
   * from the first such failure this app session rather than from the
   * connector's persisted stall record: that record also runs through time
   * spent offline, and after days offline the first server error on
   * reconnecting would otherwise read as "not moving for days".
   */
  rowsFailingSince?: number
  /** Since when photo uploads have been failing without one succeeding. */
  photosFailingSince?: number
}

type Listener = () => void

const INITIAL: UploadSignals = { rowsWaitingForConnection: false }

let state: UploadSignals = INITIAL
const listeners = new Set<Listener>()

function update(next: Partial<UploadSignals>) {
  const merged = { ...state, ...next }
  if (
    merged.lastSuccessAt === state.lastSuccessAt &&
    merged.rowsWaitingForConnection === state.rowsWaitingForConnection &&
    merged.rowsFailingSince === state.rowsFailingSince &&
    merged.photosFailingSince === state.photosFailingSince
  ) {
    return
  }
  state = merged
  for (const listener of listeners) listener()
}

export function getUploadSignals(): UploadSignals {
  return state
}

export function subscribeToUploadSignals(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Rows reached the server. */
export function reportRowsUploaded(now = Date.now()) {
  update({
    lastSuccessAt: now,
    rowsWaitingForConnection: false,
    rowsFailingSince: undefined,
  })
}

/**
 * The queue head moved without uploading (parked in sync_failures), so
 * whatever was failing is no longer the head.
 */
export function reportRowQueueMoved() {
  update({ rowsWaitingForConnection: false, rowsFailingSince: undefined })
}

/** Row uploads are waiting for a usable connection or session. */
export function reportRowsWaitingForConnection() {
  update({ rowsWaitingForConnection: true, rowsFailingSince: undefined })
}

/** The queue head failed with an error that keeps it queued. */
export function reportRowQueueHeadFailing(now = Date.now()) {
  update({
    rowsWaitingForConnection: false,
    rowsFailingSince: state.rowsFailingSince ?? now,
  })
}

/** A photo file reached the server. */
export function reportPhotoUploaded(now = Date.now()) {
  update({ lastSuccessAt: now, photosFailingSince: undefined })
}

/**
 * A photo upload failed and will be retried. Failures while the device is
 * offline don't count: that's waiting for a connection, not being stuck, and
 * counting them would make the first failure after reconnecting look like a
 * long stall.
 */
export function reportPhotoUploadFailed(now = Date.now()) {
  if (!navigator.onLine) {
    if (state.photosFailingSince !== undefined) {
      update({ photosFailingSince: undefined })
    }
    return
  }
  if (state.photosFailingSince !== undefined) return
  update({ photosFailingSince: now })
}

/**
 * A photo upload was refused for good. The queue moved on past it, so it
 * isn't a sign of photos being stuck (it shows as a failure instead).
 */
export function reportPhotoUploadRefused() {
  update({ photosFailingSince: undefined })
}

/**
 * The row queue is empty. Nothing is failing or waiting any more, however
 * it got there (uploaded, parked, or the local changes were discarded), so
 * the next upload starts with a clean slate instead of inheriting an old
 * failure time or connection problem.
 */
export function reportRowQueueEmpty() {
  update({ rowsWaitingForConnection: false, rowsFailingSince: undefined })
}

/** No photo files are waiting: forget any earlier failure time. */
export function reportPhotoQueueEmpty() {
  update({ photosFailingSince: undefined })
}

/** Test helper. */
export function resetUploadSignals() {
  state = INITIAL
  for (const listener of listeners) listener()
}
